import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TIMEZONES } from "@/constants/timezones";
import { CURRENCIES } from "@/constants/currencies";
import { v4 as uuid } from "@/lib/uuid";

const newStop = (order) => ({
  id: uuid(),
  date_mode: "single",
  date: "",
  end_date: "",
  start_time: "",
  timezone: "Asia/Kolkata",
  city: "",
  venue: "",
  ticket_url: "",
  cta_label: "Get Tickets",
  currency: "INR",
  price: null,
  order,
});

export function TourStopsEditor({ stops, onChange }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const addStop = () => onChange([...stops, newStop(stops.length)]);
  const updateStop = (id, patch) => onChange(stops.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const removeStop = (id) => onChange(stops.filter((s) => s.id !== id));

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const reordered = [...stops];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    onChange(reordered.map((s, idx) => ({ ...s, order: idx })));
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div className="space-y-4" data-testid="tour-stops-editor">
      {stops.map((stop, i) => (
        <div
          key={stop.id}
          onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
          onDrop={() => handleDrop(i)}
          className={`rounded-xl border p-4 space-y-3 transition-opacity ${overIndex === i && dragIndex !== null && dragIndex !== i ? "border-indigo-400" : "border-white/10"} ${dragIndex === i ? "opacity-40" : ""} bg-white/[0.03]`}
          data-testid={`tour-stop-row-${stop.id}`}
        >
          <div className="flex items-center gap-2">
            <span
              draggable
              onDragStart={(e) => { setDragIndex(i); e.dataTransfer.effectAllowed = "move"; }}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              className="cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60 shrink-0"
              data-testid={`tour-stop-drag-handle-${stop.id}`}
            >
              <i className="fa-solid fa-grip-vertical text-xs" />
            </span>
            <Input
              value={stop.city}
              onChange={(e) => updateStop(stop.id, { city: e.target.value })}
              placeholder="City"
              className="flex-1 bg-transparent border-white/15"
              data-testid={`tour-stop-city-${stop.id}`}
            />
            <Input
              value={stop.venue}
              onChange={(e) => updateStop(stop.id, { venue: e.target.value })}
              placeholder="Venue"
              className="flex-1 bg-transparent border-white/15"
              data-testid={`tour-stop-venue-${stop.id}`}
            />
            <button onClick={() => removeStop(stop.id)} className="shrink-0 p-2 text-white/40 hover:text-rose-400" data-testid={`remove-tour-stop-${stop.id}`}>
              <i className="fa-solid fa-trash text-xs" />
            </button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Date</Label>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg bg-black/30 p-0.5">
                <button
                  onClick={() => updateStop(stop.id, { date_mode: "single" })}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${stop.date_mode !== "range" ? "bg-indigo-500 text-white" : "text-slate-400"}`}
                  data-testid={`tour-stop-date-mode-single-${stop.id}`}
                >
                  Single Date
                </button>
                <button
                  onClick={() => updateStop(stop.id, { date_mode: "range" })}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${stop.date_mode === "range" ? "bg-indigo-500 text-white" : "text-slate-400"}`}
                  data-testid={`tour-stop-date-mode-range-${stop.id}`}
                >
                  Date Range
                </button>
              </div>
              <Input
                type="date"
                value={stop.date}
                onChange={(e) => updateStop(stop.id, { date: e.target.value })}
                className="bg-transparent border-white/15"
                data-testid={`tour-stop-date-${stop.id}`}
              />
              {stop.date_mode === "range" && (
                <>
                  <span className="text-slate-500 text-xs">to</span>
                  <Input
                    type="date"
                    value={stop.end_date}
                    onChange={(e) => updateStop(stop.id, { end_date: e.target.value })}
                    className="bg-transparent border-white/15"
                    data-testid={`tour-stop-end-date-${stop.id}`}
                  />
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Start Time</Label>
              <Input
                type="time"
                value={stop.start_time}
                onChange={(e) => updateStop(stop.id, { start_time: e.target.value })}
                className="bg-transparent border-white/15"
                data-testid={`tour-stop-start-time-${stop.id}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Timezone (as set by organizer)</Label>
              <Select value={stop.timezone} onValueChange={(v) => updateStop(stop.id, { timezone: v })}>
                <SelectTrigger className="bg-transparent border-white/15" data-testid={`tour-stop-timezone-${stop.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-slate-500">Fans see this converted automatically to their own local time on the public page.</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Ticket Link</Label>
              <Input
                value={stop.ticket_url}
                onChange={(e) => updateStop(stop.id, { ticket_url: e.target.value })}
                placeholder="https://..."
                className="bg-transparent border-white/15"
                data-testid={`tour-stop-ticket-url-${stop.id}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Button CTA</Label>
              <Input
                value={stop.cta_label ?? "Get Tickets"}
                onChange={(e) => updateStop(stop.id, { cta_label: e.target.value })}
                placeholder="Get Tickets"
                className="bg-transparent border-white/15"
                data-testid={`tour-stop-cta-input-${stop.id}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Currency</Label>
              <Select value={stop.currency} onValueChange={(v) => updateStop(stop.id, { currency: v })}>
                <SelectTrigger className="bg-transparent border-white/15" data-testid={`tour-stop-currency-${stop.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Starting Price (optional)</Label>
              <Input
                type="number"
                min="0"
                value={stop.price ?? ""}
                onChange={(e) => updateStop(stop.id, { price: e.target.value === "" ? null : e.target.value })}
                placeholder="e.g. 25"
                className="bg-transparent border-white/15"
                data-testid={`tour-stop-price-${stop.id}`}
              />
            </div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={addStop} className="border-white/15 border-dashed hover:bg-white/10 w-full" data-testid="add-tour-stop-button">
        <i className="fa-solid fa-plus mr-2" /> Add Tour Stop
      </Button>
    </div>
  );
}
